import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiErrorCode, apiErrorMessage } from "@/api/client";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md card p-6 space-y-3">
          <h2 className="text-xl font-semibold">無効なリンクです</h2>
          <p className="text-sm text-gray-700">
            パスワード再設定リンクが正しくありません。
            ログイン画面の「パスワードをお忘れの方」から再度ご依頼ください。
          </p>
          <div className="pt-3 border-t text-center">
            <Link to="/login" className="text-brand-700 hover:underline text-sm">
              ログイン画面へ戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("パスワードは 8 文字以上で入力してください。");
      return;
    }
    if (password !== confirm) {
      setError("確認用パスワードが一致しません。");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === "INVALID_RESET_TOKEN") {
        setError("リンクが無効か期限切れです。再度パスワード再設定をご依頼ください。");
      } else {
        setError(apiErrorMessage(err, "パスワード設定に失敗しました。"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold text-brand-700">かんたん在庫</h1>
          <p className="mt-2 text-sm text-gray-600">パスワード設定</p>
        </div>

        {done ? (
          <div className="card p-6 space-y-4">
            <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              パスワードを設定しました。ログインしてサービスをご利用ください。
            </div>
            <div className="pt-3 border-t text-center">
              <Link to="/login" className="btn-primary inline-block">
                ログイン画面へ
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="card p-6 space-y-4">
            <h2 className="text-xl font-semibold">新しいパスワードを設定</h2>

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <div>
              <label className="label" htmlFor="password">新しいパスワード（8 文字以上）</label>
              <input
                id="password"
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="label" htmlFor="confirm">確認用パスワード</label>
              <input
                id="confirm"
                type="password"
                className="input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={submitting}
              />
            </div>

            <button type="submit" className="btn-primary w-full" disabled={submitting}>
              {submitting ? "設定中..." : "パスワードを設定する"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
